import { Component, computed, input } from '@angular/core';
import { DecimalPipe, NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { IHairdresser } from '../../interfaces/hairdresser.interface';
import { formatActivityArea } from '../../utils/activity-area.utils';
import { AVATAR_PLACEHOLDER, IMAGE_PLACEHOLDER } from '../../constants/app.constants';

/**
 * Carte coiffeuse au format de la boutique : visuel portrait 3:4, note et
 * badge posés sur l'image, avatar + nom + zone en dessous. Partagée par
 * l'accueil, la liste des coiffeuses, les pages ville, la fiche coiffure et
 * les favoris, pour que toutes les grilles du site se ressemblent.
 */
@Component({
  selector: 'braidsbook-hairdresser-card',
  imports: [DecimalPipe, NgOptimizedImage, RouterLink, TranslatePipe],
  template: `
    @if (hairdresser(); as h) {
      <a [routerLink]="['/hairdressers', h.username]"
        class="group flex flex-col gap-2 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-main-500 focus-visible:ring-offset-2">
        <div class="relative aspect-[3/4] rounded-xl overflow-hidden bg-primary-light border border-gray-100">
          @if (image()) {
            <img [ngSrc]="image()!" [alt]="name()" fill [priority]="priority()"
              class="object-cover object-top group-hover:scale-[1.03] transition-transform duration-300">
          } @else {
            <div class="absolute inset-0 flex items-center justify-center">
              <img [ngSrc]="placeholder" [alt]="name()" width="56" height="56" class="size-14 opacity-30">
            </div>
          }
          @if (badgeKey()) {
            <span class="absolute top-2 left-2 rounded-full bg-white/95 shadow-sm text-[11px] font-semibold text-gray-900 px-2.5 py-1">
              {{ badgeKey()! | translate }}
            </span>
          }
          @if (h.note) {
            <span class="absolute bottom-2 right-2 rounded-full bg-white/95 shadow-sm text-sm font-semibold text-main-900 px-2.5 py-1 tabular-nums">
              <span class="text-amber-400">★</span> {{ h.note | number: '1.1-1' }}
            </span>
          }
        </div>
        <div class="px-1 flex items-center gap-2 min-w-0">
          <div class="relative size-8 shrink-0 rounded-full overflow-hidden border-2 border-main-500 bg-gray-100">
            <img [ngSrc]="h.pp_link || avatarPlaceholder" [alt]="name()" fill class="object-cover">
          </div>
          <div class="min-w-0">
            <div class="font-medium text-gray-900 leading-snug truncate group-hover:text-main-600 transition-colors">{{ name() }}</div>
            @if (area()) {
              <div class="text-xs text-gray-500 truncate">{{ area() }}</div>
            }
          </div>
        </div>
      </a>
    }
  `
})
export class HairdresserCardComponent {
  hairdresser = input.required<IHairdresser>();
  /** Clé de traduction d'un badge posé sur le visuel (ex. « Nouveau »). */
  badgeKey = input<string | null>(null);
  /** À activer pour les premières cartes visibles (image LCP). */
  priority = input<boolean>(false);

  /** Peu de coiffeuses ont une couverture : on retombe sur la photo de profil. */
  image = computed(() => {
    const h = this.hairdresser();
    return h.cover_link || h.pp_link || null;
  });
  name = computed(() => {
    const h = this.hairdresser();
    return h.username?.trim() || `${h.firstname ?? ''} ${h.lastname ?? ''}`.trim();
  });
  area = computed(() => formatActivityArea(this.hairdresser().activity_area));

  protected readonly placeholder = IMAGE_PLACEHOLDER;
  protected readonly avatarPlaceholder = AVATAR_PLACEHOLDER;
}
